import React, { FC, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MutationFunction } from 'react-apollo';
import { Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Formik, Form as FormikForm } from 'formik';
import { compose } from 'recompose';

import { CreateClientInvitationComponent, CreateClientInvitationInput, S3ObjectInput } from 'api';
import { CSVUpload, PaddedPaper } from 'components';
import { useNotify } from 'hooks';

import Actions from '../components/PeopleInvite/Actions';
import Form from '../components/PeopleInvite/Form';

import { PeopleInviteSchema } from '../validations';

const styles = (theme: Theme) =>
  createStyles({
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(4),
    },
  });

interface IPropsEnhanced extends RouteComponentProps, WithStyles<typeof styles> {}

const enhancers = compose<IPropsEnhanced, {}>(withRouter, withStyles(styles));

const PeopleInvite: FC<IPropsEnhanced> = ({ location, classes }) => {
  const [working, setWorking] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  const initialValues: CreateClientInvitationInput = {
    uploadfile: null,
    // @ts-ignore-next-line
    email_addresses: (location.state && location.state.emails) || [],
    subject: null,
    message: null,
    email_from_address: null,
  };

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function handleCompleted() {
    setWorking(false);

    notify.info({
      component: t('admin:emailsSent'),
    });
  }

  function handleSubmit(mutationFn: MutationFunction, values: CreateClientInvitationInput) {
    const options = {
      variables: { data: values },
    };
    setWorking(true);

    return mutationFn(options);
  }

  return (
    <CreateClientInvitationComponent onError={handleError} onCompleted={handleCompleted}>
      {mutationFn => (
        <Formik
          onSubmit={values => handleSubmit(mutationFn, values)}
          initialValues={initialValues}
          validationSchema={PeopleInviteSchema}>
          {formik => (
            <FormikForm>
              <PaddedPaper>
                <Grid container justify="space-between" alignItems="center">
                  <Grid item>
                    <Typography color="inherit" variant="subtitle2">
                      {t('admin:invitePeople')}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <CSVUpload
                      s3Object={formik.values.uploadfile}
                      onS3Object={(uploadfile: S3ObjectInput) => {
                        formik.setFieldValue('uploadfile', uploadfile);
                        formik.setFieldValue('email_addresses', []);
                      }}
                    />
                  </Grid>
                </Grid>
                <Divider className={classes.divider} />
                <Form />
                <Actions working={working} />
              </PaddedPaper>
            </FormikForm>
          )}
        </Formik>
      )}
    </CreateClientInvitationComponent>
  );
};

export default enhancers(PeopleInvite);
